/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Component } from '@appCanvasCommon/component-system/Component';
import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { EventBinder } from '@appPlanApplication/interaction';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { DisplayObject } from 'pixi.js-legacy';
import { IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { CursorType } from '@appCanvasCommon/events/cursor-type.enum';
import { HammerEvent } from '@appCanvasCommon/interaction/EventBinder';
import { ActiveLine } from '../../../../app-data/models/ActiveLine';
import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { IActiveColumn } from '../../../../modules/plan/services/active-column.service';
import { snapping } from 'utils';
import { IFbdbPullSpace } from '../../../../modules/plan/services/pull-space.service';
import { PullColumn } from '@touchplan/common';
import { LoggerServiceFactory } from '../../../../common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { analyticsEventsService } from '../../../services/analytics/AnalyticsEventsService';
import { GhostLineDrawer } from '@appPlanApplication/timeline/ghost-line/GhostLineDrawer';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { DragAtEdge } from '../../../../app-data/models/DragAtEdge';
import { RenderType } from 'unitTestUtils/SplitDataMock';
import { PromisePeriodService } from '../../../../modules/plan/services/promise-period.service';
import { PromisePeriod } from '../../../../app-data/models/PromisePeriod';
/**
 * Magic numbers and other settings related to active line drag logic
 */
const settings = {
	dragOffset: 12, // Magic number in original drag code...no clue why an offset of 12 was chosen
	snapStickiness: 150, // Magic number that represents how sticky the drag operation is
	pullColumnDiscriminator: `pullColumn`, // Special key that identifies a pull column
};

/**
 * ActiveLineDrag
 * Component which handles all of the drag operations for the ActiveLine
 */
export class ActiveLineDrag extends Component implements ComponentInterface {
	/**
	 * Instance of EventBinder, which connects HammerJS events to the PixiJS event system.
	 * @private
	 */
	private _binder!: EventBinder | null;
	/**
	 * We store the last ActiveLine so we can synchronously access data while performing drag calculations
	 * @private
	 */
	private _cachedActiveLine: ActiveLine | null = null;
	/**
	 * We store the last ActiveLine so we can synchronously access data while performing drag calculations
	 * @private
	 */
	private _cachedPromisePeriodList!: Map<string, PromisePeriod> | null;
	/**
	 * We store the current active columns so we can synchronously access column data when performing drag calculations
	 * @private
	 */
	private _cachedActiveColumns!: IActiveColumn[] | null;
	/**
	 * We store the current pull columns so we can synchronously access column data when performing drag calculations
	 * @private
	 */
	private _cachedPullColumns!: IFbdbPullSpace | null;
	/**
	 * Cleans up any internal subscriptions when this component is destroyed
	 * @protected
	 */
	private _shutdownSubject$: Subject<void> | null = new Subject<void>();
	/**
	 * The starting X position when the active line starts getting dragged
	 * @private
	 */
	private _startX: number = 0;
	/**
	 * The last column that was found by the snapping function
	 * @private
	 */
	private _foundColumn!: IActiveColumn | PullColumn | null;
	/**
	 * Logger instance for...logging
	 * @private
	 */
	private _logger: ILoggerService = LoggerServiceFactory.create('activeLineHandling');
	/**
	 * Reference of the ghost line we want to use to handle the ghost visual
	 */
	private _ghostLine!: GhostLineDrawer;
	/**
	 * Object storing the label text of the active line. We pass this in so we have access to the exact active line state
	 * when we start dragging and can create an accurate ghost.
	 * @private
	 */
	private _label!: HighResolutionText;
	/**
	 * Instance of our DragAtEdge helper which facilitates detecting when we are at the edge of the screen
	 * @private
	 */
	private _dragAtEdge!: DragAtEdge | null;
	/**
	 * We need to create a new DragAtEdge for each dragStart event in case the screen size changes in between drags
	 * This array is to track subscriptions so we can properly cleanup all of our subscribers once a drag ends.
	 * @private
	 */
	private _dragCleanup: Subscription[] = [];

	/**
	 * The setup method is called once all properties on a component have been decorated by the ComponentManager.
	 * From this point on, you can safely use this.getParent() and this.getManager().
	 * Arguments passed into setup are passed in via the ComponentManager.add(name, component, ...args)
	 * @param canvasAngularLand - Dependency object for Canvas / Angular connection
	 * @param parent - The display object that this component attaches to
	 * @param ghostLine - The ghost line that appears when we start dragging
	 * @param	label - We pass in the label of the active line to access the exact text.
	 */
	public override setup(
		canvasAngularLand: CanvasAngularLand,
		parent: DisplayObject,
		ghostLine: GhostLineDrawer,
		label: HighResolutionText
	): void {
		this.canvasAngularLand = canvasAngularLand;
		this._ghostLine = ghostLine;
		this._label = label;
		this._binder = new EventBinder(parent);
		parent.eventMode = EventMode.Static;
		parent.cursor = CursorType.Pointer;

		this.canvasAngularLand.activeLineService.changed$
			.pipe(takeUntil(this._shutdownSubject$ as Subject<void>))
			.subscribe((activeLine: ActiveLine | null) => {
				this._cachedActiveLine = activeLine;
			});

		this.canvasAngularLand.activeColumnService.changed$
			.pipe(takeUntil(this._shutdownSubject$ as Subject<void>))
			.subscribe((columns: IActiveColumn[] | null) => (this._cachedActiveColumns = columns));

		this.canvasAngularLand.pullSpaceService.changed$
			.pipe(takeUntil(this._shutdownSubject$ as Subject<void>))
			.subscribe((pullSpace: IFbdbPullSpace | null) => (this._cachedPullColumns = pullSpace));

		this.canvasAngularLand.promisePeriodService.changed$
			.pipe(takeUntil(this._shutdownSubject$ as Subject<void>))
			.subscribe((promisePeriod: Map<string, PromisePeriod> | null) => {
				this._cachedPromisePeriodList = promisePeriod;
			});

		this._binder.add('hammer-panstart', this.dragStart.bind(this));
		this._binder.add('hammer-pan', this.drag.bind(this));
		this._binder.add('hammer-panend', this.dragEnd.bind(this));
		this._binder.add('hammer-holddragstart', this.dragStart.bind(this));
		this._binder.add('hammer-holddrag', this.drag.bind(this));
		this._binder.add('hammer-holddragend', this.dragEnd.bind(this));
	}

	/**
	 * Handles the start of a drag operation
	 * Initializes drag state and switches active line to edit mode
	 */
	public dragStart(_: HammerEvent): void {
		if (!this.canDrag()) {
			return;
		}
		this.canvasAngularLand.activeLineService.switchToEdit();
		this._startX = this._cachedActiveLine?.view.activeLineX as number;
		this._foundColumn = null;
		this._ghostLine.enable(this._label.text, this._startX);

		// We are creating a new subscription on drag start instead of in setup so we have the most accurate
		// camera data. Setting up in setup can result in stale camera data otherwise.
		this._dragAtEdge = new DragAtEdge(this.canvasAngularLand.cameraService.screen);
		const edgeSubscription: Subscription = this._dragAtEdge.change$.subscribe((event) => {
			// Forcing deltaY to 0 because we only want to pan along the X-axis
			this.canvasAngularLand.cameraService.translate(-event.deltaX, 0);
		});

		this._dragCleanup.push(edgeSubscription);
	}

	/**
	 * Handles the continuous drag movement and runs on every drag tick
	 * Contains the main snapping logic
	 */
	public drag(event: HammerEvent): void {
		if (!this.canDrag()) {
			return;
		}

		(this._dragAtEdge as DragAtEdge).update(event.center);

		let left: number =
			this._startX + (event.deltaX as number) / this.canvasAngularLand.canvasConnector.camera.scale.x;
		let al: number =
			this._startX + settings.dragOffset * this.canvasAngularLand.canvasConnector.camera.scale.x;
		let max: number | undefined;

		const promisePeriodService: PromisePeriodService = this.canvasAngularLand.promisePeriodService;
		const currentId: string = promisePeriodService.currentPromisePeriodId;

		if (this._cachedPromisePeriodList !== null) {
			const promisePeriod: PromisePeriod | undefined = this._cachedPromisePeriodList.get(currentId);
			if (
				promisePeriod !== undefined &&
				this._cachedActiveLine?.view.activeLineDate === promisePeriod.view.endDate
			) {
				max = this.canvasAngularLand.activeColumnService.getXFromDate(promisePeriod.view.endDate as string);
			}
		}

		if (max === undefined) {
			max = -Infinity;
		}

		//the performance... it hurts
		// 2025 note: this is original code from the 2015 - 2018 period
		this.canvasAngularLand.tickets.syncList.forEach((t) => {
			// Only consider tickets that are to the left of our active line boundary
			// and update max if this ticket extends further right than our current max
			const right: number = t.getTheoreticalRightEdge();
			if (right <= al && right > (max as number)) {
				max = right;
			}
		});

		//abort if on active side
		if (left < max) {
			left = max;
		}

		// Perform snapping calculation against multiple target arrays:
		// 1. Active columns (with finishX property)
		// 2. Starting position (as a single-element array)
		// 3. Pull columns (with finishX property)
		let hit = snapping(
			left,
			[this._cachedActiveColumns, [this._startX], (this._cachedPullColumns as IFbdbPullSpace).columns || []],
			settings.snapStickiness,
			[(item: IActiveColumn) => item.finishX, (x: number) => x, (item: PullColumn) => item.finishX]
		);

		if (hit) {
			left = hit.loc;
			if (hit.loc !== hit.item) {
				this._foundColumn = hit.item;
			} else {
				this._foundColumn = null;
			}
		} else {
			left = (this._cachedActiveLine as ActiveLine).view.activeLineX as number;
		}

		if (this._foundColumn) {
			this.canvasAngularLand.activeLineService.setLineX(left);
			// Update the active line date based on the type of column we snapped to
			if (this.isPullColumn(this._foundColumn)) {
				// Pull columns use plannedFinish date
				this.canvasAngularLand.activeLineService.setLineDate(
					(<PullColumn>this._foundColumn)?.plannedFinish as string
				);
			} else {
				// Active columns use activeDay date
				this.canvasAngularLand.activeLineService.setLineDate((<IActiveColumn>this._foundColumn)?.activeDay);
			}
		}
	}

	/**
	 * Handles the end of a drag operation
	 * Processes the final drop location of the active line, persists data to firebase and leaves edit mode
	 */
	public dragEnd(_: HammerEvent): void {
		this._ghostLine.disable();
		if (!this.canDrag()) {
			return;
		}
		// Failed to find a column to snap to, so do nothing
		if (!this._foundColumn) {
			return;
		}

		(this._dragAtEdge as DragAtEdge).cancel();

		this._dragCleanup.forEach((sub) => sub.unsubscribe());
		this._dragCleanup = [];

		this._logger.log('found column', this._foundColumn);
		// Check if the active line actually moved to a different position
		if (Math.floor(this._startX) !== Math.floor(this._foundColumn.finishX as number)) {
			Logging.notice('Activating');
		}

		// Special handling if we activate a pull column
		if (this.isPullColumn(this._foundColumn)) {
			const foundPullColumn: PullColumn = this._foundColumn as PullColumn;
			const pullColumnIndex: number = foundPullColumn.pullColumn as number;
			this.canvasAngularLand.plansApi.setActivatedPullColumn(
				this.canvasAngularLand.plan.syncObject,
				pullColumnIndex
			);
			analyticsEventsService.draggedActiveLine(
				(this._cachedActiveLine as ActiveLine).view.activeLineDate as string,
				pullColumnIndex,
				foundPullColumn.plannedFinish,
				(this._cachedPullColumns as IFbdbPullSpace).columns.length - pullColumnIndex,
				this.canvasAngularLand.planId
			);
			return;
		}

		let extra = { initiatedBy: 'systemOnActiveLineChange' };
		this.canvasAngularLand.firebaseSnapshotsService
			.takeSnapshot$(this.canvasAngularLand.projectId, this.canvasAngularLand.planId, extra)
			.subscribe({
				next: () => {
					this._logger.log('snapshot success');
					this.canvasAngularLand.activeLineService.save(RenderType.Edit);
					this.canvasAngularLand.activeLineService.switchToDatabase();
				},
				error: (error) => {
					this._logger.error('Error taking snapshot on active line drag end', error);
				},
			});
	}

	/**
	 * Cleanup method called when the component is destroyed
	 * Ensures all subscriptions and resources are properly disposed
	 */
	public override destroy(opts?: IDestroyOptions) {
		super.destroy(opts);
		this._shutdownSubject$?.next();
		this._binder?.destroy();
		this._dragAtEdge = null;
		this._cachedActiveLine = null;
		this._cachedActiveColumns = null;
		this._cachedPullColumns = null;
		this._foundColumn = null;
		this._binder = null;
		this._shutdownSubject$ = null;
	}

	/**
	 * Determines whether dragging is currently allowed
	 * Checks for required data and user permissions
	 * @returns true if dragging should be enabled, false otherwise
	 */
	private canDrag(): boolean {
		return (
			this._cachedActiveLine !== null &&
			this._cachedPromisePeriodList !== null &&
			this._cachedActiveColumns !== null &&
			this._cachedPullColumns !== null &&
			this.canvasAngularLand.planAccessCheck.isAdminSync &&
			!this.canvasAngularLand.planAccessCheck.hasRestrictedAccessSync
		);
	}

	/**
	 * Type guard to determine if an object is a PullColumn or IActiveColumn
	 * Uses the discriminator property to differentiate between the two types
	 * Pretty much have to do this because the old snapping function is not typesafe
	 * @param thing - The column object to check
	 * @returns true if the object is a PullColumn, false if it's an IActiveColumn
	 */
	private isPullColumn(thing: IActiveColumn | PullColumn): thing is PullColumn {
		// Force checking for undefined because a pullColumn with a `pullColumn` value of 0 will return as falsy
		return (<any>thing)[settings.pullColumnDiscriminator] !== undefined;
	}
}
